import React from 'react';

interface AISearchOptimizationProps {
  title: string;
  summary: string;
  keyPoints: string[];
  entities?: {
    name: string;
    type: string;
    description: string;
  }[];
  faqs?: {
    question: string;
    answer: string;
  }[];
}

export const AISearchOptimization: React.FC<AISearchOptimizationProps> = ({
  title,
  summary,
  keyPoints,
  entities = [],
  faqs = []
}) => {
  return (
    <>
      {/* AI Summary Block - Optimized for AI Crawlers */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-50 py-12 px-6 rounded-2xl mb-12" itemScope itemType="https://schema.org/WebPageElement">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-ai-generate text-white text-xl"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3" itemProp="name">{title}</h2>
              <p className="text-lg text-gray-700 leading-relaxed" itemProp="description">{summary}</p>
            </div>
          </div>
          
          {keyPoints.length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Key Points:</h3>
              <ul className="space-y-2">
                {keyPoints.map((point, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-indigo-600 text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>

      {/* Entity Definitions - For AI Understanding */}
      {entities.length > 0 && (
        <section className="mb-12" itemScope itemType="https://schema.org/ItemList">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">What You Need to Know</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {entities.map((entity, index) => (
              <div 
                key={index} 
                className="bg-white p-6 rounded-xl border-2 border-gray-100 hover:border-indigo-200 transition-colors"
                itemScope 
                itemType="https://schema.org/Thing"
                itemProp="itemListElement"
              >
                <h3 className="text-lg font-bold text-gray-900 mb-2" itemProp="name">{entity.name}</h3>
                <p className="text-sm text-indigo-600 mb-3" itemProp="additionalType">{entity.type}</p>
                <p className="text-gray-700" itemProp="description">{entity.description}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* FAQ Section - Structured for AI */}
      {faqs.length > 0 && (
        <section className="mb-12" itemScope itemType="https://schema.org/FAQPage">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className="bg-white p-6 rounded-xl border-2 border-gray-100"
                itemScope 
                itemProp="mainEntity" 
                itemType="https://schema.org/Question"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-start gap-3" itemProp="name">
                  <i className="ri-question-line text-indigo-600 text-xl flex-shrink-0 mt-0.5"></i>
                  {faq.question}
                </h3>
                <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                  <p className="text-gray-700 pl-9" itemProp="text">{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Schema.org JSON-LD for AI Crawlers */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": title,
          "description": summary,
          "mainEntity": faqs.length > 0 ? {
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          } : undefined
        })}
      </script>
    </>
  );
};
